import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError } from "../api/client";
import { useDeleteModel, useModels } from "../api/models";
import type { ModelListItem } from "../api/types";
import { SectionHeader } from "../assets/SectionHeader";
import { preloadViewer } from "../assets/inspector";
import { messageFor } from "../copy/errors";
import { formatRelative } from "../ui/format";
import { useNow } from "../ui/useNow";
import { Button, PageHeader, Pill, Skeleton } from "../ui";

export const STUDIO_EYEBROW = "— Objects · Design studio";

const GRID = "m-0 grid list-none grid-cols-2 gap-4 p-0 lg:grid-cols-4";
const SKELETONS = 8;

/** What the browser asks before a design is deleted. */
export function deleteDesignText(published: boolean): string {
  return (
    "Delete this design? Its history is deleted." +
    (published ? " The object already in Objects stays." : "")
  );
}

/** "You have 200 designs, the most allowed. Delete one to start another." */
export function designLimitText(limit: number): string {
  return messageFor("design_limit", { limit });
}

function CardMenu(props: { item: ModelListItem; onDelete(): void }) {
  const menu = useRef<HTMLDetailsElement>(null);
  return (
    <details ref={menu} className="absolute right-1 top-1">
      <summary
        className="flex h-11 w-11 cursor-pointer list-none items-center justify-center rounded-full bg-white/90 text-lg font-bold leading-none text-slate-700 shadow-card hover:bg-white"
        aria-label={`More actions for ${props.item.name}`}
      >
        ⋯
      </summary>
      <div
        className="tb-card tb-card-plain absolute right-0 z-10 mt-1 flex w-36 flex-col gap-1 p-2 text-sm"
        role="menu"
      >
        <button
          type="button"
          role="menuitem"
          className="min-h-[44px] rounded px-2 py-1.5 text-left hover:bg-slate-100 sm:min-h-0"
          onClick={() => {
            if (menu.current) menu.current.open = false;
            props.onDelete();
          }}
        >
          Delete
        </button>
      </div>
    </details>
  );
}

function DesignCard(props: {
  item: ModelListItem;
  now: number;
  onDelete(): void;
}) {
  const { item } = props;
  return (
    <div className="relative" data-testid="design-card">
      <Link
        to={`/assets/studio/${encodeURIComponent(item.id)}`}
        className="block rounded-lg text-left no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand"
        // The page this leads to draws in 3D: its code starts on its way now.
        onPointerEnter={preloadViewer}
        onFocus={preloadViewer}
      >
        <span className="relative block aspect-square w-full overflow-hidden rounded-lg bg-slate-100">
          {item.built && item.thumb_url ? (
            <img
              src={item.thumb_url}
              alt={item.name}
              loading="lazy"
              className="h-full w-full object-cover"
            />
          ) : null}
          {item.built ? null : (
            <span className="absolute inset-0 flex items-center justify-center bg-slate-200">
              <Pill size="sm">Not built</Pill>
            </span>
          )}
        </span>
        <span
          className="mt-2 block break-words text-sm font-semibold text-slate-800"
          data-testid="design-name"
        >
          {item.name}
        </span>
        {item.published || item.state !== "idle" ? (
          <span className="mt-1 flex flex-wrap gap-1">
            {item.published ? (
              <Pill size="sm" tone="purple">
                In Objects
              </Pill>
            ) : null}
            {item.state !== "idle" ? (
              <Pill size="sm" tone="info">
                Working…
              </Pill>
            ) : null}
          </span>
        ) : null}
        <span className="mt-0.5 block text-xs text-slate-500">
          {formatRelative(item.updated_at, props.now)}
        </span>
      </Link>
      <CardMenu item={item} onDelete={props.onDelete} />
    </div>
  );
}

/** `/assets/studio`: the organisation's designs, the one last changed first
 *  ([design-studio §11.2], [ux §8.15]). */
export function DesignListPage() {
  const models = useModels();
  const remove = useDeleteModel();
  const now = useNow(60_000);
  const [deleted, setDeleted] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  const first = models.data?.pages[0];
  const items = models.data?.pages.flatMap((page) => page.items) ?? [];
  const full = first !== undefined && first.count >= first.limit;

  const onDelete = (item: ModelListItem) => {
    if (remove.isPending) return;
    if (!window.confirm(deleteDesignText(item.published))) return;
    setDeleted(false);
    setFailure(null);
    remove.mutate(item.id, {
      onSuccess: () => setDeleted(true),
      onError: setFailure,
    });
  };

  const newDesign = full ? (
    <Button
      type="button"
      variant="primary"
      className="min-h-[44px] disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
      disabled
      title={designLimitText(first.limit)}
    >
      New design
    </Button>
  ) : (
    <Button
      as={Link}
      to="/assets/studio/new"
      variant="primary"
      className="min-h-[44px] sm:min-h-0"
    >
      New design
    </Button>
  );

  return (
    <>
      <PageHeader
        eyebrow={STUDIO_EYEBROW}
        title="Designs"
        right={
          models.isSuccess && (items.length > 0 || full) ? newDesign : undefined
        }
      />
      <SectionHeader />
      {full ? (
        <p className="mb-4 mt-0 text-sm text-slate-700" data-testid="at-limit">
          {designLimitText(first.limit)}
        </p>
      ) : null}
      {deleted ? (
        <p role="status" className="mb-4 mt-0 text-sm text-slate-700">
          Deleted.
        </p>
      ) : null}
      {failure ? (
        <p role="alert" className="mb-4 mt-0 text-sm text-slate-700">
          {messageFor(failure instanceof ApiError ? failure.code : null)}
        </p>
      ) : null}
      {models.isPending ? (
        <ul className={GRID} aria-busy="true">
          {Array.from({ length: SKELETONS }, (_, i) => (
            <li key={i} data-testid="design-skeleton">
              <span className="block aspect-square w-full overflow-hidden rounded-lg">
                <Skeleton width="100%" height="100%" />
              </span>
              <span className="mt-2 block">
                <Skeleton width="70%" height={14} />
              </span>
              <span className="mt-1 block">
                <Skeleton width="45%" height={12} />
              </span>
            </li>
          ))}
        </ul>
      ) : models.isError ? (
        <p role="alert" className="m-0 text-sm text-slate-700">
          {messageFor(
            models.error instanceof ApiError ? models.error.code : null,
          )}
        </p>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-start gap-4">
          <p className="m-0 text-sm text-slate-700" data-testid="designs-empty">
            No designs yet. Describe an object and it is modelled for you in
            about two minutes.
          </p>
          {newDesign}
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          <ul className={GRID}>
            {items.map((item) => (
              <li key={item.id}>
                <DesignCard
                  item={item}
                  now={now}
                  onDelete={() => onDelete(item)}
                />
              </li>
            ))}
          </ul>
          {models.hasNextPage ? (
            <Button
              type="button"
              variant="ghost"
              className="min-h-[44px] w-full sm:min-h-0 sm:w-auto sm:self-center"
              disabled={models.isFetchingNextPage}
              aria-busy={models.isFetchingNextPage}
              onClick={() => void models.fetchNextPage()}
            >
              {models.isFetchingNextPage ? "Loading…" : "Load more"}
            </Button>
          ) : null}
        </div>
      )}
    </>
  );
}
